import Dexie, { Table } from 'dexie';

import { Issue, Article, Style } from '@/constants';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('Db');

// Exported so the useDb-failure recovery screen can delete exactly this
// database (indexedDB.deleteDatabase(DEXIE_DB_NAME)) without duplicating
// the literal and risking drift.
export const DEXIE_DB_NAME = 'canvasflow';

export class DexieDb extends Dexie implements Db {
	issues!: Table<Issue>;
	articles!: Table<Article>;
	styles!: Table<Style>;
	constructor() {
		super(DEXIE_DB_NAME);
		this.version(1).stores({
			issues: '&id, index, slug',
			articles: '&id, index, IssueID, slug, isFeatured',
			styles: '&id',
		});
	}

	// Dexie doesn't eagerly connect on `new Dexie()` -- it configures the
	// schema and connects lazily on the first real table operation, or
	// when `.open()` is called explicitly. This used to just be
	// `Promise.resolve()`, a no-op that resolved regardless of whether the
	// real database ever opened -- which meant a genuine open failure
	// (a version conflict, quota exhaustion, private-browsing restrictions)
	// was never caught here at all; it would only have surfaced much later,
	// at whatever random point something first touched a table, with
	// nothing awaiting it. `useDb`'s failure path (and the recovery screen
	// it drives) depends on this actually reflecting whether the database
	// opened -- confirmed by forcing a real Dexie VersionError in manual
	// testing, which this fix is what made catchable at all.
	async start(): Promise<void> {
		await this.open();
	}

	async saveIssues(issues: Array<Issue>): Promise<Array<Issue>> {
		for (let i = 0; i < issues.length; i++) {
			const issue = issues[i];
			issue.index = i;
			await this.saveIssue(issue);
		}
		return this.getIssues();
	}

	async getIssues(): Promise<Array<Issue>> {
		const issues = await this.issues.toArray();
		return issues.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
	}

	async getIssue(id: string, isSlug?: boolean): Promise<Issue | null> {
		const cachedIssue = isSlug
			? await this.issues.get({ slug: id })
			: await this.issues.get({ id });
		if (!cachedIssue) {
			return null;
		}
		return cachedIssue;
	}

	// Counterpart to `saveIssue`: removes the issue row and its articles
	// together, so a caller (the downloaded-issues flow) never has to
	// remember to call `deleteArticlesByIssue` separately.
	async deleteIssue(id: string): Promise<void> {
		await this.deleteArticlesByIssue(id);
		await this.issues.where('id').equals(id).delete();
	}

	async saveIssue(issue: Issue): Promise<Issue | null> {
		if (issue.articles) {
			await this.deleteArticlesByIssue(issue.id);
			issue.articles = issue.articles.map((article: Article) => {
				article.IssueID = `${issue.id}`;
				return article;
			});
			await this.saveArticles(clone(issue.articles));
			delete issue.articles;
		}
		await this.issues.put(issue, 'id');
		const cachedIssue = await this.getIssue(issue.slug ?? issue.id, true);
		if (cachedIssue) {
			cachedIssue.articles = await this.getArticlesByIssue(
				cachedIssue.id,
			);
		}
		return cachedIssue;
	}

	async deleteArticlesByIssue(id: string): Promise<void> {
		await this.articles.where('IssueID').equals(id).delete();
	}

	async deleteArticle(id: string): Promise<void> {
		await this.articles.where('id').equals(id).delete();
	}

	async saveArticles(articles: Array<Article>): Promise<void> {
		// An empty bulk write issues no IndexedDB request, which lets an
		// enclosing transaction (`runInTransaction`) auto-commit right here
		// and fail every step after it (Dexie's PrematureCommitError).
		if (!articles.length) {
			return;
		}
		logger.debug(`ARTICLES`, articles);
		await this.articles.bulkPut(articles);
	}

	async saveFeatureArticles(articles: Array<Article>): Promise<void> {
		logger.debug(`FEATURED ARTICLES`, articles);
		/* Remove existing feature articles in the issue */
		const issuesSet = new Set<string>([]);
		for (const article of articles) {
			if (article.IssueID) {
				issuesSet.add(`${article.IssueID}`);
			}
		}
		await this.removeFeatureArticleInIssue([...issuesSet]);
		/**************************************************/

		const featureArticles = await this.getFeatureArticles();
		const featureArticlesMap = new Map<string, Article>();
		for (const article of featureArticles) {
			featureArticlesMap.set(article.id, article);
		}
		const response = [];
		for (const article of articles) {
			const { featureImage, name, slug, thumbnail } = article;
			if (featureArticlesMap.has(article.id)) {
				const cachedArticle = featureArticlesMap.get(article.id);
				if (!cachedArticle) {
					continue;
				}
				cachedArticle.featureImage = featureImage;
				cachedArticle.name = name;
				cachedArticle.slug = slug;
				cachedArticle.thumbnail = thumbnail;
				response.push(cachedArticle);
			} else {
				response.push(article);
			}
		}
		await this.articles.bulkPut(response);
	}

	async removeFeatureArticleInIssue(issues: Array<string>) {
		for (const issue of issues) {
			await this.articles
				.where('IssueID')
				.equals(`${issue}`)
				.filter((a: Article) => !!a.isFeatured)
				.delete();
		}
	}

	async getArticlesByIssue(id: string): Promise<Array<Article>> {
		return this.articles.where('IssueID').equals(`${id}`).toArray();
	}

	// Every stored article, across every downloaded issue.
	async getAllArticles(): Promise<Array<Article>> {
		return this.articles.toArray();
	}

	async getFeatureArticles(): Promise<Article[]> {
		return await this.articles.filter((a) => !!a.isFeatured).toArray();
	}

	async getArticle(id: string, isSlug?: boolean): Promise<Article | null> {
		const cachedArticle = isSlug
			? await this.articles.get({ slug: id })
			: await this.articles.get({ id });
		if (!cachedArticle) {
			return null;
		}
		return cachedArticle;
	}

	async removeStyles(): Promise<void> {
		await this.styles.clear();
	}

	// Additive -- upserts by id (update if the style is already stored, add
	// it if not), never clears the table first. `styles` is one central
	// table shared by every downloaded issue, keyed by style id, so a style
	// several issues use is stored exactly once; a destructive clear-then-
	// replace here would evict styles other downloaded issues still need.
	// Written by `entities/downloaded-issue`'s `saveDownloadedIssue`. Use
	// `removeStyles()` for an actual wholesale wipe.
	async saveStyles(styles: Array<Style>): Promise<void> {
		// `Style.id` is `string` on the wire but arrives as a number often
		// enough to matter (every *read* here already stringifies it:
		// `getStyle`'s `{ id }`, `deleteStyles`'s own map). `id` is this
		// table's primary key (`styles: '&id'`), and IndexedDB key
		// comparison is type-sensitive -- a style put with a numeric id is
		// stored under a numeric key that no later string lookup or delete
		// can ever match. Normalizing here, at the one write path, is what
		// keeps every read/delete elsewhere actually able to find it.
		styles = uniqueBy(
			styles.map((style) => ({ ...style, id: `${style.id}` })),
			'id',
		);
		// Same empty-write guard as `saveArticles`.
		if (!styles.length) {
			return;
		}
		logger.debug(`STYLES`, styles);
		await this.styles.bulkPut(styles);
	}

	async getStyles(): Promise<Style[]> {
		return await this.styles.toArray();
	}

	// Targeted counterpart to `removeStyles()`'s wholesale clear, for
	// `entities/downloaded-issue`'s removal flow: only the styles no other
	// downloaded issue still uses are passed in. Ids are stringified for the same reason every other style lookup
	// here is -- they're `string` on the wire but arrive as numbers often
	// enough to matter.
	async deleteStyles(ids: Array<string | number>): Promise<void> {
		if (!ids.length) {
			return;
		}
		await this.styles.bulkDelete(ids.map((id) => `${id}`));
	}

	// Exactly the styles a downloaded issue owns (`Issue.styleIds`), in one
	// read -- missing ids are dropped rather than returned as `undefined`.
	async getStylesByIds(ids: Array<string | number>): Promise<Style[]> {
		if (!ids.length) {
			return [];
		}
		const found = await this.styles.bulkGet(ids.map((id) => `${id}`));
		return found.filter((style): style is Style => !!style);
	}

	// Runs `fn` as one read-write transaction over all three tables, so a
	// multi-step write (save an issue + its styles, or remove one + the
	// styles only it used) can't interleave with another one halfway
	// through. `fn` must only await this db's own operations -- Dexie
	// commits a transaction as soon as it sees a non-Dexie await.
	async runInTransaction<T>(fn: () => Promise<T>): Promise<T> {
		return this.transaction(
			'rw',
			this.issues,
			this.articles,
			this.styles,
			fn,
		);
	}

	async getStyle(id: string): Promise<Style | null> {
		const style = await this.styles.get({ id });
		if (!style) {
			return null;
		}
		return style;
	}

	async clear(): Promise<void> {
		// Previously fire-and-forget (not awaited) -- a caller relying on
		// `clear()`'s own returned promise to know the wipe actually landed
		// (e.g. `session-teardown.ts`'s login/logout wipe) could resolve
		// before any table was genuinely empty.
		await Promise.all([
			this.issues.clear(),
			this.articles.clear(),
			this.styles.clear(),
		]);
	}
}

export interface Db {
	start(): Promise<void>;
	clear(): Promise<void>;
	saveIssues(issues: Array<Issue>): Promise<Array<Issue>>;
	getIssues(): Promise<Array<Issue>>;
	getIssue(id: string, isSlug?: boolean): Promise<Issue | null>;
	saveIssue(issue: Issue): Promise<Issue | null>;
	deleteIssue(id: string): Promise<void>;
	deleteArticlesByIssue(id: string): Promise<void>;
	deleteArticle(id: string): Promise<void>;
	saveArticles(articles: Array<Article>): Promise<void>;
	saveFeatureArticles(articles: Array<Article>): Promise<void>;
	getFeatureArticles(): Promise<Array<Article>>;
	getArticlesByIssue(id: string): Promise<Array<Article>>;
	getAllArticles(): Promise<Array<Article>>;
	getArticle(id: string, isSlug?: boolean): Promise<Article | null>;
	removeStyles(): Promise<void>;
	saveStyles(styles: Array<Style>): Promise<void>;
	getStyles(): Promise<Array<Style>>;
	getStyle(id: string): Promise<Style | null>;
	getStylesByIds(ids: Array<string | number>): Promise<Array<Style>>;
	deleteStyles(ids: Array<string | number>): Promise<void>;
	runInTransaction<T>(fn: () => Promise<T>): Promise<T>;
}

export function uniqueBy<T>(inputs: Array<T>, property: keyof T): Array<T> {
	const response: Array<T> = [];
	const mapResponse: Record<string, T> = {};
	for (const input of inputs) {
		mapResponse[`${String(input[property])}`] = input;
	}

	for (const input in mapResponse) {
		response.push(mapResponse[input]);
	}

	return response;
}

function clone<T>(obj: T): T {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}
